import { FEDERAL_COUNTRIES, INDIA_JURISDICTION_FILTER_CODE } from '@/constants/jurisdictions';
import { CountryCode, EUCountryEnum, EU_AGGREGATOR_COUNTRY_CODE } from '@/types/location';
import { ValueOf } from '@/types/utils';

// Maps synthetic filter codes back to the real ISO country codes sent to the API.
// India uses 'ZZ_IN' in the dropdown to avoid colliding with Indiana's US state code 'IN'.
const JURISDICTION_FILTER_CODE_MAP: Record<string, string> = {
  [INDIA_JURISDICTION_FILTER_CODE]: CountryCode.IN,
};

const normalizeJurisdictionCode = (code: string): string => JURISDICTION_FILTER_CODE_MAP[code] ?? code;

/**
 * Determines if a jurisdiction code represents a country-level jurisdiction
 * in the **filter dropdown** context (values from ALL_JURISDICTIONS_BY_REGION).
 * India is intentionally NOT matched via FEDERAL_COUNTRIES here — its dropdown value is the
 * synthetic 'ZZ_IN' code, not 'IN', to avoid collision with Indiana's US state code.
 * Use `isCountryLevelRegistration` when working with raw ISO country codes from registration
 * objects (e.g. registrationData.country_code).
 */
export const isCountryLevelJurisdiction = (jurisdiction: string): boolean => {
  const isEU =
    Object.values(EUCountryEnum).includes(jurisdiction as EUCountryEnum) || jurisdiction === EU_AGGREGATOR_COUNTRY_CODE;

  // Exclude CountryCode.IN: in filter context India uses INDIA_JURISDICTION_FILTER_CODE ('ZZ_IN').
  // Including 'IN' here would make Indiana ('IN') incorrectly treated as country-level.
  const isFederalCountry =
    FEDERAL_COUNTRIES.includes(jurisdiction as ValueOf<typeof CountryCode>) && jurisdiction !== CountryCode.IN;

  const isUnitedKingdom = jurisdiction === CountryCode.GB;

  const isIndia = jurisdiction === INDIA_JURISDICTION_FILTER_CODE;

  // 'US' is the "All US States" aggregator option — country-level so it maps to country_code=US
  // (all US filings) rather than a single state_code. Individual state codes never equal 'US'.
  const isUnitedStates = jurisdiction === CountryCode.US;

  return isEU || isFederalCountry || isUnitedKingdom || isIndia || isUnitedStates;
};

/**
 * Determines if a raw ISO country code represents a country-level jurisdiction,
 * for use with **registration objects** (e.g. registrationData.country_code).
 * Unlike `isCountryLevelJurisdiction`, this function accepts raw ISO codes directly
 * (including 'IN' for India) because registration country_code is always an ISO code,
 * never a filter dropdown value like 'ZZ_IN'.
 */
export const isCountryLevelRegistration = (countryCode: string): boolean => {
  const isEU =
    Object.values(EUCountryEnum).includes(countryCode as EUCountryEnum) || countryCode === EU_AGGREGATOR_COUNTRY_CODE;

  const isFederalCountry = FEDERAL_COUNTRIES.includes(countryCode as ValueOf<typeof CountryCode>);

  const isUnitedKingdom = countryCode === CountryCode.GB;

  return isEU || isFederalCountry || isUnitedKingdom;
};

export interface CommonFilterParams {
  filterAssignee?: string | string[] | null;
  filterJurisdiction?: string | string[] | null;
  filterStatus?: string | string[] | null;
  filterType?: string | null;
  filterWaitingOn?: string | string[] | null;
  filterOrganization?: string | null;
  filterBillingPlan?: string | string[] | null;
  filterThirdParty?: string | null; // 'all' | 'yes' | 'no'
  filterCurrentPeriodValidationPriority?: string | null; // 'all' | 'yes'
}

const applyAssigneeFilter = (params: Record<string, any>, filterAssignee?: string | string[] | null) => {
  if (filterAssignee == null) return;
  const selected = (Array.isArray(filterAssignee) ? filterAssignee : [filterAssignee]).filter(
    value => value && value !== 'all'
  );
  if (selected.length === 0) return;
  params.task_assignee = selected.map(value => (value === 'UNASSIGNED' ? 'unassigned' : value)).join(',');
};

const applyJurisdictionFilter = (params: Record<string, any>, filterJurisdiction?: string | string[] | null) => {
  if (filterJurisdiction == null) return;
  const selected = (Array.isArray(filterJurisdiction) ? filterJurisdiction : [filterJurisdiction]).filter(
    value => value && value !== 'all'
  );
  if (selected.length === 0) return;

  const countryCodes: string[] = [];
  const stateCodes: string[] = [];
  for (const code of selected) {
    const normalized = normalizeJurisdictionCode(code);
    if (isCountryLevelJurisdiction(code)) {
      countryCodes.push(normalized);
    } else {
      stateCodes.push(normalized);
    }
  }
  // Country codes stay an array so registrations can serialize country_code__in.
  // Filings join the array back to country_code before the request.
  if (countryCodes.length > 0) {
    params.country_code = countryCodes;
  }
  if (stateCodes.length === 1) {
    params.state_code = stateCodes[0];
  } else if (stateCodes.length > 1) {
    params.state_code = stateCodes.join(',');
  }
};

const applySimpleFilter = (params: Record<string, any>, key: string, value?: string | null) => {
  if (value && value !== 'all') {
    params[key] = value;
  }
};

const applyStatusFilter = (params: Record<string, any>, filterStatus?: string | string[] | null) => {
  if (filterStatus == null) return;
  const selected = (Array.isArray(filterStatus) ? filterStatus : [filterStatus]).filter(
    value => value && value !== 'all'
  );
  if (selected.length === 0) return;
  params.task_status = selected.length === 1 ? selected[0] : selected;
};

const applyWaitingOnFilter = (params: Record<string, any>, filterWaitingOn?: string | string[] | null) => {
  if (filterWaitingOn == null) return;
  const selected = (Array.isArray(filterWaitingOn) ? filterWaitingOn : [filterWaitingOn]).filter(
    value => value && value !== 'all' && value !== 'NONE'
  );
  if (selected.length === 0) return;
  params.task_waiting_on = selected.join(',');
};

const applyBillingPlanFilter = (params: Record<string, any>, filterBillingPlan?: string | string[] | null) => {
  if (filterBillingPlan == null) return;
  const selected = (Array.isArray(filterBillingPlan) ? filterBillingPlan : [filterBillingPlan]).filter(
    value => value && value !== 'all'
  );
  if (selected.length === 0) return;
  params.billing_plan = selected.join(',');
};

const applyThirdPartyFilter = (params: Record<string, any>, value?: string | null) => {
  if (!value || value === 'all') return;
  params.third_party_enabled = value === 'yes' ? 'true' : 'false';
};

const applyCurrentPeriodValidationPriorityFilter = (params: Record<string, any>, value?: string | null) => {
  // BE only supports the positive filter (true); omit for All.
  if (!value || value === 'all') return;
  if (value === 'yes') {
    params.current_period_validation_priority = 'true';
  }
};

export const buildFilterParams = (
  filters: CommonFilterParams,
  options?: {
    typeFieldName?: string;
    additionalFilters?: Record<string, any>; // For entity-specific filters
  }
): Record<string, any> => {
  const params: Record<string, any> = {};
  const { typeFieldName = 'task_type', additionalFilters } = options || {};

  applyAssigneeFilter(params, filters.filterAssignee);
  applyJurisdictionFilter(params, filters.filterJurisdiction);
  applyStatusFilter(params, filters.filterStatus);
  applySimpleFilter(params, typeFieldName, filters.filterType);
  applyThirdPartyFilter(params, filters.filterThirdParty);
  applyCurrentPeriodValidationPriorityFilter(params, filters.filterCurrentPeriodValidationPriority);

  applyWaitingOnFilter(params, filters.filterWaitingOn);
  applyBillingPlanFilter(params, filters.filterBillingPlan);

  if (filters.filterOrganization) {
    params.organization_id = filters.filterOrganization;
  }

  // Add any additional filters (ie for filings)
  if (additionalFilters) {
    Object.assign(params, additionalFilters);
  }

  return params;
};
